import PropTypes from 'prop-types'
import { VehicleObjectDetailsModel } from '../../models/vehicle/vehicle-object-details-model'
import { controlFlagsPropType } from '../common/control-flags-prop-type'
import { idPropType } from '../common/id-prop-type'
import { iso8601PropType } from '../common/iso-8601-prop-type'
import { accelerationVehicleTypePropType } from './acceleration-vehicle-type-prop-type'
import { driverTerminalMapsPropType } from './driver-terminal-maps-prop-type'
import { objectFeatureQuotaPropType } from './object-feature-quota-prop-type'
import { objectRecurringCustomerBookingPropType } from './object-recurring-customer-booking-prop-type'

const {
    ACCELERATION_VEHICLE_TYPE,
    AXLE_WEIGHT_FRONT,
    AXLE_WEIGHT_REAR,
    CAK,
    CONTRACT_NUMBER,
    CONTROL_FLAGS,
    CUSTOMER_NUMBER,
    DESCRIPTION,
    DIESEL_EXHAUST_FLUID_LEVEL_PERCENTAGE,
    DRIVER_TERMINAL_CONNECTED,
    DRIVER_TERMINAL_MAPS,
    DRIVER_TERMINAL_MODEL,
    DRIVER_TERMINAL_NAV_SOFTWARE_VERSION,
    DRIVER_TERMINAL_SERIAL_NUMBER,
    DRIVER_TERMINAL_TTWD_SOFTWARE_VERSION,
    ENGINE_TOTAL_HOURS,
    FUEL_LEVEL,
    FUEL_LEVEL_PERCENTAGE,
    FUEL_TYPE,
    GROSS_WEIGHT,
    HEIGHT,
    LENGTH,
    LINK_APP_VERSION,
    LINK_DEVICE_TYPE,
    LINK_OS_VERSION,
    LINK_SERIAL_NUMBER,
    MAX_SPEED,
    NET_WEIGHT,
    OBJECT_FEATURE_QUOTAS,
    OBJECT_ID,
    OBJECT_RECURRING_CUSTOMER_BOOKINGS,
    REFERENCE_NUMBER,
    REGISTRATION_DATE,
    TANK_SIZE,
    TARIFF,
    VALID_FROM,
    VALID_TO,
    VEHICLE_REGISTRATION_TYPE,
    VIN,
    VSD_GENERATION_ID,
    WIDTH
} = VehicleObjectDetailsModel

/**
 * @typedef {Object} VehicleObjectDetails
 * @property {AccelerationVehicleType} [accelerationVehicleType] - User selected acceleration class
 * @property {number} [axleWeightFront] - The front axle weight
 * @property {number} [axleWeightRear] - The rear axle weight
 * @property {string} [cak] - The Customer Activation Key
 * @property {string} [contractNumber] - The contract number of the corresponding WEBFLEET contract
 * @property {Array<string>} [controlFlags] - A limited selection of control flags of the object
 * @property {string} [customerNumber] - The customer number
 * @property {string} [description] - The description of the vehicle
 * @property {number} [dieselExhaustFluidLevelPercentage] - The description of the vehicle
 * @property {boolean} [driverTerminalConnected] - The current diesel exhaust fluid level in percent
 * @property {VehicleObjectMapDetails[]} [driverTerminalMaps] - A list of details of maps installed on the driver terminal
 * @property {string} [driverTerminalModel] - The model of the driver terminal (PND)
 * @property {string} [driverTerminalNavSoftwareVersion] - The version of the navigation software of the driver terminal (PND)
 * @property {string} [driverTerminalSerialNumber] - The serial number of the driver terminal (PND)
 * @property {string} [driverTerminalTtwdSoftwareVersion] - The version of the TTWD (work deamon) of the driver terminal (PND)
 * @property {number} [engineTotalHours] - Total engine hours in seconds
 * @property {number} [fuelLevel] - The fuelLevel in liters
 * @property {number} [fuelLevelPercentage] - The fuelLevel in percent
 * @property {number} [grossWeight] - The vehicle gross weight
 * @property {number} [height] - The vehicle height in mm
 * @property {number} [length] - The vehicle length in mm
 * @property {string} [linkAppVersion] - The version of the LINK application
 * @property {string} [linkDeviceType] - The device type of the LINK (for example, DRESDEN, BREMEN, etc...)
 * @property {string} [linkOsVersion] - The version of the LINK OS
 * @property {string} [linkSerialNumber] - The serial number of the LINK
 * @property {number} [maxSpeed] - Max speed of the vehicle in km/h
 * @property {number} [netWeight] - The net weight of the vehicle in kg
 * @property {Array<ObjectFeatureQuota>} [objectFeatureQuotas] - A list of allowed and used quotas for booked features
 * @property {Id} [objectId] - The ID of the object
 * @property {Array<ObjectRecurringCustomerBooking>} [objectRecurringCustomerBookings] - A list of additionally booked features (recurring)
 * @property {string} [referenceNumber] - The reference number
 * @property {string} [registrationDate] - The registration date of the object. Despite this is a date, for legacy reasons it accepts any character-based input
 * @property {string} [tankSize] - The object tank size in l
 * @property {string} [tariff] - The name of the tariff
 * @property {ISO8601} [validFrom] - The contract start date
 * @property {ISO8601} [validTo] - The contract end date
 * @property {string} [vehicleRegistrationType] - The registration type, used for dutch logbook ('BUSINESS' or 'PRIVATE'
 * @property {string} [vin] - The VIN
 * @property {number} [vsdGenerationId] - The generation id of the vehicle
 * @property {number} [width] - The width length in mm
 */
export const vehicleObjectDetailsPropType = PropTypes.shape({
    [ACCELERATION_VEHICLE_TYPE]: accelerationVehicleTypePropType,
    [AXLE_WEIGHT_FRONT]: PropTypes.number,
    [AXLE_WEIGHT_REAR]: PropTypes.number,
    [CAK]: PropTypes.string,
    [CONTRACT_NUMBER]: PropTypes.string,
    [CONTROL_FLAGS]: controlFlagsPropType,
    [CUSTOMER_NUMBER]: PropTypes.string,
    [DESCRIPTION]: PropTypes.string,
    [DIESEL_EXHAUST_FLUID_LEVEL_PERCENTAGE]: PropTypes.number,
    [DRIVER_TERMINAL_CONNECTED]: PropTypes.bool,
    [DRIVER_TERMINAL_MAPS]: PropTypes.arrayOf(driverTerminalMapsPropType),
    [DRIVER_TERMINAL_MODEL]: PropTypes.string,
    [DRIVER_TERMINAL_NAV_SOFTWARE_VERSION]: PropTypes.string,
    [DRIVER_TERMINAL_SERIAL_NUMBER]: PropTypes.string,
    [DRIVER_TERMINAL_TTWD_SOFTWARE_VERSION]: PropTypes.string,
    [ENGINE_TOTAL_HOURS]: PropTypes.number,
    [FUEL_LEVEL]: PropTypes.number,
    [FUEL_LEVEL_PERCENTAGE]: PropTypes.number,
    [FUEL_TYPE]: PropTypes.string,
    [GROSS_WEIGHT]: PropTypes.number,
    [HEIGHT]: PropTypes.number,
    [LENGTH]: PropTypes.number,
    [LINK_APP_VERSION]: PropTypes.string,
    [LINK_DEVICE_TYPE]: PropTypes.string,
    [LINK_OS_VERSION]: PropTypes.string,
    [LINK_SERIAL_NUMBER]: PropTypes.string,
    [MAX_SPEED]: PropTypes.number,
    [NET_WEIGHT]: PropTypes.number,
    [OBJECT_FEATURE_QUOTAS]: PropTypes.arrayOf(objectFeatureQuotaPropType),
    [OBJECT_ID]: idPropType,
    [OBJECT_RECURRING_CUSTOMER_BOOKINGS]: PropTypes.arrayOf(objectRecurringCustomerBookingPropType),
    [REFERENCE_NUMBER]: PropTypes.string,
    [REGISTRATION_DATE]: PropTypes.string,
    [TANK_SIZE]: PropTypes.number,
    [TARIFF]: PropTypes.string,
    [VALID_FROM]: iso8601PropType,
    [VALID_TO]: iso8601PropType,
    [VEHICLE_REGISTRATION_TYPE]: PropTypes.string,
    [VIN]: PropTypes.string,
    [VSD_GENERATION_ID]: PropTypes.number,
    [WIDTH]: PropTypes.number
})
